import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Vanya from '../assets/vanya.jpeg';
import men from '../assets/menc.jpeg';
import women from '../assets/womenc.jpeg'
import ProductCard from "../components/ProductCard";
import { supabase } from "../supabaseClient";

function Category() {
  const { category, subcategory } = useParams();

  const displaySubcategory = subcategory
    ? subcategory.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())
    : null;

  const displayCategory = category?.charAt(0).toUpperCase() + category?.slice(1);

  const dbCategory = category?.toLowerCase();
  const dbSubcategory = subcategory?.replace(/-/g, " ").toLowerCase();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryInfo, setCategoryInfo] = useState(null);

  // Fetch products
  useEffect(() => {
    async function fetchProducts() {
      setLoading(true);
      try {
        console.log("Fetching - Category:", dbCategory, "Subcategory:", dbSubcategory);

        if (subcategory) {
          const { data, error } = await supabase
            .from("products")
            .select("*")
            .eq("is_live", true)
            .ilike("category", dbCategory)
            .ilike("subcategory", `%${dbSubcategory}%`);

          if (error) {
            console.error("Error:", error);
            setProducts([]);
          } else {
            console.log(`Found ${data?.length || 0} products for ${dbCategory}/${dbSubcategory}`);
            setProducts(data || []);
          }
        } else {
          const { data, error } = await supabase
            .from("products")
            .select("*")
            .eq("is_live", true)
            .ilike("category", dbCategory);

          if (error) {
            console.error("Error:", error);
            setProducts([]);
          } else {
            console.log(`Found ${data?.length || 0} products for category ${dbCategory}`);
            setProducts(data || []);
          }
        }
      } catch (err) {
        console.error("Unexpected error:", err);
        setProducts([]);
      }
      setLoading(false);
    }

    if (dbCategory) fetchProducts();
  }, [category, subcategory, dbCategory, dbSubcategory]);

  // Category display info
  useEffect(() => {
    const categoryImages = {
      men: {
        desktop: men,
        mobile: men,
      },
      women: {
        desktop: women,
        mobile: women,
      },
      homeware: {
        desktop: Vanya,
        mobile: Vanya,
      },
    };

    const categoryDescriptions = {
      men: "Elevate your everyday style with our timeless men's collection",
      women: "Discover elegance and sophistication in our women's collection",
      homeware: "Transform your space with artisanal home decor",
    };

    const images = categoryImages[dbCategory] || categoryImages.homeware;

    setCategoryInfo({
      desktopImage: images.desktop,
      mobileImage: images.mobile,
      description: categoryDescriptions[dbCategory] || "Discover our curated collection",
    });
  }, [dbCategory]);

  const title = subcategory ? displaySubcategory : displayCategory;

  // Reusable Banner Component
  const Banner = ({ desktopImage, mobileImage, title, subtitle }) => (
    <section className="relative w-full">
      {/* Mobile Banner — shown only on small screens */}
      <div className="block md:hidden w-full h-[260px] relative">
        <img
          src={mobileImage}
          alt={title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-charcoal/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 text-center">
          <h1 className="font-serif text-3xl capitalize">{title}</h1>
          {subtitle && (
            <p className="text-xs mt-2 tracking-wide opacity-80 max-w-xs">{subtitle}</p>
          )}
        </div>
      </div>

      {/* Desktop Banner — shown only on md and above */}
      <div className="hidden md:block w-full h-[480px] relative">
        <img
          src={desktopImage}
          alt={title}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-charcoal/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-6">
          <h1 className="font-serif text-5xl capitalize">{title}</h1>
          {subtitle && (
            <p className="text-sm mt-3 tracking-wide opacity-80 max-w-md">{subtitle}</p>
          )}
        </div>
      </div>
    </section>
  );

  // Homeware main category page
  if (category === "homeware" && !subcategory) {
    const subcategories = [
      {
        name: "Vanya",
        path: "/category/homeware/vanya",
        image: "https://zrzwgmmwbeavzkhsbsmu.supabase.co/storage/v1/object/public/products/Vanya%20Banner.jpeg",
        description: "Eco-friendly handcrafted Vanya products",
      },
    ];

    return (
      <div className="bg-cream min-h-screen">
        <Banner
          desktopImage={categoryInfo?.desktopImage}
          mobileImage={categoryInfo?.mobileImage}
          title="Homeware Collections"
          subtitle="Artisanal pieces for your home"
          objectPosition="bottom"
        />

        <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {subcategories.map((sub) => (
              <Link
                key={sub.name}
                to={sub.path}
                className="group relative overflow-hidden aspect-[16/9] rounded-2xl shadow-lg"
              >
                <img
                  src={sub.image}
                  alt={sub.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <h3 className="font-serif text-2xl mb-2">{sub.name}</h3>
                  <p className="text-sm opacity-90">{sub.description}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Explore Collection <ArrowRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    );
  }

  // Normal category/subcategory view
  return (
    <div className="bg-cream min-h-screen">

      <Banner
        desktopImage={categoryInfo?.desktopImage}
        mobileImage={categoryInfo?.mobileImage}
        title={title}
        subtitle={categoryInfo?.description}
      />

      {/* Products Section */}
      <section className="py-16 max-w-7xl mx-auto px-6 md:px-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-3 border-[#e8e0d5] border-t-[#ba8245] rounded-full animate-spin" />
            <p className="text-gray-500 mt-4">Loading treasures...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 bg-white/50 rounded-2xl">
            <div className="w-20 h-20 bg-[#f0ebe4] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-[#ba8245]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h2 className="font-serif text-2xl text-charcoal mb-2">Coming Soon</h2>
            <p className="text-[#6B665F]">
              {subcategory
                ? `No products found in ${title} yet.`
                : `Our ${title} collection is being curated.`}
            </p>
            <Link
              to="/categories"
              className="inline-block mt-6 text-bronze hover:text-bronze-dark underline"
            >
              Browse other categories
            </Link>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-10 flex-wrap gap-4">
              <h2 className="font-serif text-2xl text-charcoal capitalize">
                {title} Collection
              </h2>
              <span className="text-sm text-[#6B665F]">
                {products.length} {products.length === 1 ? "Product" : "Products"}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-16">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

export default Category;